import Hex from './hex';
import SVG from './svg';

/**
 * The SVGPath class draws a single vector path with the shape defined in the commands property.
 *
 * This is useful when you want to draw icons, arrows etc.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/d} for examples
 */
export default class SVGPath extends SVG {
	/**
	 * A path definition is a list of path commands where each command is composed of a command letter and numbers that represent the command parameters.
	 *
	 * @param value A path string defining the shape or null
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/d} for syntax
	 */
	public set commands(value: null | string) {
		assertIsValidCommands(value);
		this.#commands = value;
		this.#commandsChanged = true;
		this.invalidateProperties();
	}

	public get commands(): null | string {
		return this.#commands;
	}

	/**
	 * The strokeCap property is a presentation attribute defining the shape to be used at the end of open subpaths when they are stroked.
	 *
	 * @param value The shape of the stroke ending
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke-linecap} for syntax
	 */
	public set strokeCap(value: 'butt' | 'round' | 'square') {
		assertIsValidCap(value);
		this.#strokeCap = value;
		this.#strokeCapChanged = true;
		this.invalidateProperties();
	}

	public get strokeCap(): 'butt' | 'round' | 'square' {
		return this.#strokeCap;
	}

	/**
	 * The strokeColor property is a presentation attribute defining the color used to paint the outline of the shape.
	 *
	 * @param value The color of the stroke or null
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke} for syntax
	 */
	public set strokeColor(value: Hex | null) {
		assertIsValidStrokeColor(value);
		this.#strokeColor = value;
		this.#strokeColorChanged = true;
		this.invalidateProperties();
	}

	public get strokeColor(): Hex | null {
		return this.#strokeColor;
	}

	/**
	 * The strokeJoin property is a presentation attribute defining the shape to be used at the corners of paths when they are stroked.
	 *
	 * @param value The path corner shape
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke-linejoin} for syntax
	 */
	public set strokeJoin(value: 'bevel' | 'miter' | 'round') {
		assertIsValidJoin(value);
		this.#strokeJoin = value;
		this.#strokeJoinChanged = true;
		this.invalidateProperties();
	}

	public get strokeJoin(): 'bevel' | 'miter' | 'round' {
		return this.#strokeJoin;
	}

	/**
	 * The weight property defines the stroke's thickness in pixels.
	 *
	 * @param value The thickness of the stroke in pixels, has to be a positive number default 1
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke-width} for syntax
	 */
	public set strokeWeight(value: number) {
		assertIsPositiveNumber(value);
		this.#strokeWeight = value;
		this.#strokeWeightChanged = true;
		this.invalidateProperties();
	}

	public get strokeWeight(): number {
		return this.#strokeWeight;
	}

	#commands: null | string;
	#commandsChanged: boolean;

	#path: SVGPathElement;

	#strokeCap: 'butt' | 'round' | 'square';
	#strokeCapChanged: boolean;
	#strokeColor: Hex | null;
	#strokeColorChanged: boolean;
	#strokeJoin: 'bevel' | 'miter' | 'round';
	#strokeJoinChanged: boolean;
	#strokeWeight: number;
	#strokeWeightChanged: boolean;

	public constructor() {
		super();
		this.#commands = null;
		this.#commandsChanged = true;
		this.#path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
		this.#strokeCap = 'round';
		this.#strokeCapChanged = true;
		this.#strokeColor = null;
		this.#strokeColorChanged = true;
		this.#strokeJoin = 'round';
		this.#strokeJoinChanged = true;
		this.#strokeWeight = 1;
		this.#strokeWeightChanged = true;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#commandsChanged) {
			this.#commitCommands();
		}
		if (this.#strokeCapChanged) {
			this.#commitStrokeCap();
		}
		if (this.#strokeColorChanged) {
			this.#commitStrokeColor();
		}
		if (this.#strokeJoinChanged) {
			this.#commitStrokeJoin();
		}
		if (this.#strokeWeightChanged) {
			this.#commitStrokeWeight();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.svg.appendChild(this.#path);
	}

	#commitCommands(): void {
		this.#commandsChanged = false;
		if (this.#commands) {
			this.#path.setAttribute('d', this.#commands);
		} else {
			this.#path.setAttribute('d', '');
		}
	}

	#commitStrokeCap(): void {
		this.#strokeCapChanged = false;
		this.#path.setAttribute('stroke-linecap', this.#strokeCap);
	}

	#commitStrokeColor(): void {
		this.#strokeColorChanged = false;
		if (this.#strokeColor) {
			this.#path.setAttribute('stroke', this.#strokeColor.toStyleString());
		} else {
			this.#path.setAttribute('stroke', '');
		}
	}

	#commitStrokeJoin(): void {
		this.#strokeJoinChanged = false;
		this.#path.setAttribute('stroke-linejoin', this.#strokeJoin);
	}

	#commitStrokeWeight(): void {
		this.#strokeWeightChanged = false;
		this.#path.setAttribute('stroke-width', this.#strokeWeight.toString());
	}
}
customElements.define('ac-svg-path', SVGPath);

function assertIsPositiveNumber(value: unknown): asserts value is number {
	if (typeof value === 'number' && value > 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be positive number`);
}

function assertIsValidCap(value: unknown): asserts value is 'butt' | 'round' | 'square' {
	if (value === 'butt' || value === 'round' || value === 'square') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be 'butt', 'round' or 'square'`);
}

function assertIsValidCommands(value: unknown): asserts value is null | string {
	if (typeof value === 'string' || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be string or null`);
}

function assertIsValidJoin(value: unknown): asserts value is 'bevel' | 'miter' | 'round' {
	if (value === 'bevel' || value === 'miter' || value === 'round') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be 'bevel', 'miter' or 'round'`);
}

function assertIsValidStrokeColor(value: unknown): asserts value is Hex {
	if (value instanceof Hex || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an instance of Hex or null`);
}
